import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Boxes } from 'lucide-react';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useItems, type Item } from '@/api/capacities';
import { paginationView } from '@/pages/customers/pagination-summary';
import { CategoryTabs } from './category-tabs';
import { ItemsToolbar } from './items-filter-row';
import { CATEGORY_TABS, resolveCategory } from './list-configs/category-cards-config';
import {
  EMPTY_FILTERS,
  filterValue,
  type ItemFilters,
} from './list-configs/item-filter-fields';
import { itemColumns, itemDetailPath } from './list-configs/item-column-definitions';
import { exportItemsCsv } from './items-csv-export';

const PAGE_SIZE = 20;
const DEFAULT_CATEGORY = 'server';
const CATEGORY_VALUES = new Set(CATEGORY_TABS.map((t) => t.value));

/** Debounce a fast-changing value (search box) to throttle server refetches. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Unified inventory list — the 1:1 legacy Warehouse Items view. A selectable
 * category count-card row (server / cpu / ram / disk / gpu / network card /
 * switch / laptop / module) drives the primary `category` filter; a secondary
 * dropdown row (type · status · condition · owner · location · supplier · model)
 * plus a debounced search push the remaining server-side filters. The Export
 * button writes the current page rows to CSV (client-side; no export API).
 * Read-only: rows link to the equipment/component detail routes.
 */
export function ItemsListSection() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<ItemFilters>(EMPTY_FILTERS);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);

  // Active category lives in the URL (`?category=…`) so a filtered inventory view
  // is shareable/bookmarkable. Unknown values fall back to the default.
  const categoryParam = searchParams.get('category') ?? '';
  const categoryTab = CATEGORY_VALUES.has(categoryParam) ? categoryParam : DEFAULT_CATEGORY;
  const resolvedCategory = resolveCategory(categoryTab);

  const resetToFirstPage = () => setPage(1);

  const onSelectCategory = (tab: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('category', tab);
        return next;
      },
      { replace: true },
    );
    setFilters(EMPTY_FILTERS);
    setRawQuery('');
    resetToFirstPage();
  };

  const onFiltersChange = (next: ItemFilters) => {
    setFilters(next);
    resetToFirstPage();
  };

  const params = {
    page,
    page_size: PAGE_SIZE,
    category: resolvedCategory,
    search: query || undefined,
    item_type: filterValue(filters.item_type),
    status: filterValue(filters.status),
    condition: filterValue(filters.condition),
    owner: filterValue(filters.owner),
    location_id: filterValue(filters.location_id),
    supplier_id: filterValue(filters.supplier_id),
    model_id: filterValue(filters.model_id),
    rack_id: filterValue(filters.rack_id),
  };
  const { data, isLoading, isError, isFetching, refetch } = useItems(params);

  const rows: Item[] = data?.results ?? [];
  const pagination = data?.pagination;
  const pageCount = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? rows.length;

  const onExport = () => exportItemsCsv(rows, categoryTab);

  const footer = useMemo(() => {
    const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
    return (
      <Pagination
        page={page}
        pageCount={view.pageCount}
        onPageChange={setPage}
        summary={`${view.summary}${isFetching ? ' …' : ''}`}
      />
    );
  }, [page, rows.length, total, pageCount, isFetching]);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <CategoryTabs value={categoryTab} onChange={onSelectCategory} />

      <ItemsToolbar
        filters={filters}
        onFiltersChange={onFiltersChange}
        query={rawQuery}
        onQueryChange={(v) => {
          setRawQuery(v);
          resetToFirstPage();
        }}
        category={resolvedCategory}
        onExport={onExport}
        exportDisabled={rows.length === 0}
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Failed to load inventory"
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 24 }} />
          ))}
        </div>
      ) : (
        <DataTable
          columns={itemColumns}
          data={rows}
          getRowId={(r) => r.id}
          onRowClick={(r) => navigate(itemDetailPath(r))}
          emptyState={
            <EmptyState
              icon={<Boxes size={24} />}
              title="No inventory found"
              description="Try a different category, adjust the filters, or clear the search."
            />
          }
          footer={footer}
        />
      )}
    </div>
  );
}
